import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Download, Trash2, Search, Lock, Eye, EyeOff,
  Mail, Phone, MapPin, Calendar, Filter, RefreshCw, Check, Archive,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import SEO from "../components/SEO";
import {
  adminLogin, fetchLeads, getAdminToken, clearAdminToken,
  updateLeadStatus, clearAllLeadsApi,
} from "../lib/api";

const SOURCE_LABELS = {
  hero: "Hero Form",
  suburb: "Suburb Page",
  quote: "Quote Form",
  contact: "Contact Form",
};

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-[#faf3eb] text-[#b8866e]" },
  { value: "contacted", label: "Contacted", color: "bg-blue-50 text-blue-700" },
  { value: "booked", label: "Booked", color: "bg-green-50 text-green-700" },
  { value: "archived", label: "Archived", color: "bg-gray-100 text-gray-600" },
];

const Admin = () => {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const data = await fetchLeads();
      setItems(data.leads || []);
    } catch (e) {
      if (e.message === "Unauthorized") {
        setAuthed(false);
        toast.error("Session expired");
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const interval = setInterval(load, 10000); // auto-refresh every 10s
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);
    };
  }, [authed, load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await adminLogin(pwd);
      setAuthed(true);
      toast.success("Welcome back");
    } catch {
      toast.error("Wrong password");
    }
  };

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (sourceFilter !== "all" && i.source !== sourceFilter) return false;
      if (statusFilter !== "all" && (i.status || "new") !== statusFilter) return false;
      if (!filter.trim()) return true;
      const q = filter.toLowerCase();
      return Object.values(i).some(
        (v) => typeof v === "string" && v.toLowerCase().includes(q)
      );
    });
  }, [items, filter, sourceFilter, statusFilter]);

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("Nothing to export");
      return;
    }
    const keys = ["created_at", "source", "suburb_page", "status", "name", "email",
                  "phone", "service", "suburb", "frequency", "bedrooms", "bathrooms",
                  "notes", "message"];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = [keys.join(",")];
    filtered.forEach((row) => rows.push(keys.map((k) => escape(row[k])).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sterling-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} leads`);
  };

  const changeStatus = async (id, status) => {
    try {
      await updateLeadStatus(id, status);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const clearAll = async () => {
    if (!confirm("Delete ALL leads forever? This cannot be undone.")) return;
    try {
      await clearAllLeadsApi();
      setItems([]);
      toast.success("All leads cleared");
    } catch {
      toast.error("Clear failed");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const counts = useMemo(() => {
    const c = { all: items.length };
    Object.keys(SOURCE_LABELS).forEach((k) => {
      c[k] = items.filter((i) => i.source === k).length;
    });
    return c;
  }, [items]);

  // ---------- Login screen ----------
  if (!authed) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <SEO title="Admin" description="The Sterling Touch admin." path="/admin" noindex />
        <form
          onSubmit={submit}
          className="w-full max-w-sm bg-white rounded-3xl border border-[#ead8c9] p-8 shadow-[0_20px_60px_-20px_rgba(138,93,72,0.15)]"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-[#faf3eb] flex items-center justify-center">
              <Lock size={20} className="text-sterling-dark" />
            </div>
            <h1 className="font-serif-display text-3xl text-ink mt-4">Admin</h1>
            <p className="text-sm text-ink/60 mt-1">Sterling Touch lead dashboard</p>
          </div>
          <label className="text-xs text-ink/70 uppercase tracking-wider mt-8 block">
            Password
          </label>
          <div className="relative mt-1.5">
            <Input
              value={pwd}
              type={showPwd ? "text" : "password"}
              onChange={(e) => setPwd(e.target.value)}
              className="h-11 bg-white border-[#e8d9cc] focus-visible:ring-sterling rounded-lg pr-10"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink"
            >
              {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <Button
            type="submit"
            className="mt-5 w-full h-11 rounded-full bg-ink hover:bg-[#1a1410] text-cream"
          >
            Sign in
          </Button>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-1 text-xs text-ink/50 hover:text-ink"
          >
            <ArrowLeft size={12} /> Back to site
          </Link>
        </form>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="min-h-screen bg-cream">
      <SEO title="Lead Dashboard" description="Sterling Touch leads" path="/admin" noindex />
      <header className="sticky top-0 z-40 bg-white border-b border-[#ead8c9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#daae9c]">
              Sterling Touch
            </div>
            <div className="font-serif-display text-2xl text-ink leading-tight">
              Lead Dashboard
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} className="rounded-full border-ink/20" disabled={loading}>
              <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={exportCsv} className="rounded-full border-ink/20">
              <Download size={14} className="mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => { clearAdminToken(); setAuthed(false); }}
              className="rounded-full border-ink/20"
            >
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            ["Total", counts.all, "all"],
            ["Hero", counts.hero || 0, "hero"],
            ["Suburb Page", counts.suburb || 0, "suburb"],
            ["Quote Page", counts.quote || 0, "quote"],
            ["Contact Form", counts.contact || 0, "contact"],
          ].map(([label, value, key]) => (
            <button
              key={key}
              onClick={() => setSourceFilter(key)}
              className={`p-4 rounded-2xl text-left transition-all border ${
                sourceFilter === key
                  ? "bg-white border-sterling-dark shadow-[0_10px_30px_-15px_rgba(138,93,72,0.25)]"
                  : "bg-white/70 border-[#ead8c9] hover:border-sterling-dark"
              }`}
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#daae9c]">
                {label}
              </div>
              <div className="font-serif-display text-3xl text-ink mt-1">{value}</div>
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search name, email, suburb…"
              className="pl-9 h-11 bg-white border-[#e8d9cc] rounded-lg"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[["all", "All"], ["new", "New"], ["contacted", "Contacted"],
              ["booked", "Booked"], ["archived", "Archived"]].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setStatusFilter(v)}
                className={`px-3 h-8 rounded-full text-xs border transition-colors ${
                  statusFilter === v
                    ? "bg-ink text-cream border-ink"
                    : "bg-white text-ink border-[#ead8c9] hover:border-sterling-dark"
                }`}
              >
                {l}
              </button>
            ))}
            <div className="flex items-center gap-1 text-xs text-ink/60 ml-2">
              <Filter size={12} />
              {filtered.length}/{items.length}
            </div>
            <button
              onClick={clearAll}
              className="inline-flex items-center gap-1 text-[#b8866e] hover:text-red-700 text-xs ml-2"
            >
              <Trash2 size={12} /> Clear
            </button>
          </div>
        </div>

        {/* List */}
        <div className="mt-6 space-y-3">
          {filtered.length === 0 && !loading && (
            <div className="text-center py-20 bg-white border border-[#ead8c9] rounded-2xl">
              <div className="font-serif-display text-xl text-ink">No leads yet.</div>
              <div className="text-sm text-ink/60 mt-2">
                When someone submits a form, it’ll appear here within seconds.
              </div>
            </div>
          )}
          {filtered.map((item) => {
            const status = item.status || "new";
            const statusOpt = STATUS_OPTIONS.find((s) => s.value === status);
            return (
              <div
                key={item.id}
                className="bg-white border border-[#ead8c9] rounded-2xl p-5 hover:border-sterling-dark transition-colors"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-serif-display text-xl text-ink">
                        {item.name || "Unknown"}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-[#faf3eb] text-[#b8866e] border border-[#ead8c9]">
                        {SOURCE_LABELS[item.source] || item.source}
                      </span>
                      {item.suburb_page && (
                        <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-[#fdfbf7] text-ink/60 border border-[#ead8c9]">
                          /cleaning/{item.suburb_page}
                        </span>
                      )}
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${statusOpt.color}`}>
                        {statusOpt.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-ink/70">
                      {item.email && (
                        <a href={`mailto:${item.email}`} className="inline-flex items-center gap-1 hover:text-ink">
                          <Mail size={12} /> {item.email}
                        </a>
                      )}
                      {item.phone && (
                        <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1 hover:text-ink">
                          <Phone size={12} /> {item.phone}
                        </a>
                      )}
                      {item.suburb && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={12} /> {item.suburb}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-ink/50 inline-flex items-center gap-1">
                    <Calendar size={12} />
                    {formatDate(item.created_at)}
                  </div>
                </div>

                {(item.service || item.frequency || item.bedrooms || item.bathrooms) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {item.service && (
                      <span className="text-xs px-3 py-1 rounded-full bg-[#faf3eb] text-[#b8866e] border border-[#ead8c9]">
                        {item.service}
                      </span>
                    )}
                    {item.frequency && (
                      <span className="text-xs px-3 py-1 rounded-full bg-[#fdfbf7] text-ink/70 border border-[#ead8c9]">
                        {item.frequency}
                      </span>
                    )}
                    {item.bedrooms && (
                      <span className="text-xs px-3 py-1 rounded-full bg-[#fdfbf7] text-ink/70 border border-[#ead8c9]">
                        {item.bedrooms} bd
                      </span>
                    )}
                    {item.bathrooms && (
                      <span className="text-xs px-3 py-1 rounded-full bg-[#fdfbf7] text-ink/70 border border-[#ead8c9]">
                        {item.bathrooms} ba
                      </span>
                    )}
                  </div>
                )}
                {(item.notes || item.message) && (
                  <p className="mt-3 text-sm text-ink/80 italic leading-relaxed">
                    “{item.notes || item.message}”
                  </p>
                )}

                {/* Status actions */}
                <div className="mt-4 pt-3 border-t border-[#ead8c9] flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-ink/50 mr-1">
                    Move to:
                  </span>
                  {STATUS_OPTIONS.filter((s) => s.value !== status).map((s) => (
                    <button
                      key={s.value}
                      onClick={() => changeStatus(item.id, s.value)}
                      className="text-xs px-3 py-1 rounded-full bg-white text-ink border border-[#ead8c9] hover:border-sterling-dark transition-colors inline-flex items-center gap-1"
                    >
                      {s.value === "booked" && <Check size={12} />}
                      {s.value === "archived" && <Archive size={12} />}
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default Admin;
